<template>
  <div
    id="motivoModal"
    class="modal fade"
    tabindex="-1"
    role="dialog"
    aria-labelledby="motivoModalLabel"
    aria-hidden="true"
    data-backdrop="static"
  >
    <div class="modal-dialog" role="document">
      <div class="modal-content">

        <!-- Header -->
        <div class="modal-header">
          <h5 class="modal-title" id="motivoModalLabel">{{ modalTitle }}</h5>
          <!-- Cierra con data-dismiss -->
          <button type="button" class="close" data-dismiss="modal" aria-label="Cerrar">
            <span aria-hidden="true">&times;</span>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div class="form-group">
            <label for="tipoSelect">Tipo de Movimiento</label>
            <select
              id="tipoSelect"
              class="form-control"
              v-model="selectedTipo"
            >
              <option value="">-- Seleccione tipo --</option>
              <option value="salida">SALIDA</option>
              <option value="entrada">ENTRADA</option>
              <option value="traslado">TRASLADO</option>
            </select>
          </div>

          <div class="form-group" v-if="selectedTipo">
            <label for="motivoSelect">Motivo</label>
            <select
              id="motivoSelect"
              class="form-control"
              v-model.number="selectedMotivoId"
            >
              <option value="">-- Seleccione motivo --</option>
              <option :value="0">+ Nuevo motivo</option>
              <option
                v-for="m in motivosFiltrados"
                :key="m.id"
                :value="m.id"
              >
                {{ m.nombre }}
              </option>
            </select>
          </div>

          <div v-if="selectedMotivoId !== ''">
            <hr>
            <h6>{{ isNew ? 'Crear nuevo motivo' : 'Detalles del motivo' }}</h6>

            <div class="form-group">
              <label for="codigo">Código</label>
              <input
                id="codigo"
                type="text"
                class="form-control"
                v-model="form.codigo"
                disabled
              />
            </div>

            <div class="form-group">
              <label for="nombre">Nombre</label>
              <input
                id="nombre"
                type="text"
                class="form-control"
                v-model="form.nombre"
                :disabled="!isEditing"
              />
            </div>

            <div class="form-group">
              <label for="descripcion">Descripción</label>
              <textarea
                id="descripcion"
                class="form-control"
                rows="3"
                v-model="form.descripcion"
                :disabled="!isEditing"
              ></textarea>
            </div>
          </div>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <!-- Botón estándar de cierre -->
          <button type="button" class="btn btn-secondary" data-dismiss="modal">
            Cerrar
          </button>
          <button
              v-if="selectedTipo !== ''"
              class="btn btn-info"
              @click="clearForm"
            >
              Limpiar
            </button>
          <!-- Opciones según estado -->
          <template v-if="selectedMotivoId && selectedTipo !== ''">
            <button
              v-if="!isNew && !isEditing"
              class="btn btn-warning"
              @click="enableEdit"
            >
              Editar
            </button>
            <button
              v-if="!isNew && !isEditing"
              class="btn btn-danger"
              @click="deleteMotivo"
            >
              Borrar
            </button>
            
          </template>

          <button
            v-if="isEditing"
            class="btn btn-primary"
            @click="saveMotivo"
          >
            {{ isNew ? 'Crear' : 'Guardar cambios' }}
          </button>

          <!-- Botón oculto para disparar data-dismiss desde JS -->
          <button
            type="button"
            class="d-none"
            data-dismiss="modal"
          ></button>
        </div>

      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import api from '@/services/api'

// Listas y estados
const motivos          = ref([])
const selectedTipo     = ref('')
const selectedMotivoId = ref('')   // "" | 0 (nuevo) | id existente
const isEditing        = ref(false)

const form = ref({
  id: null,
  codigo: '',
  tipo_movimiento: '',
  nombre: '',
  descripcion: ''
})

// Carga inicial
onMounted(async () => {
  motivos.value = (await api.get('/motivos')).data
})

// Filtra motivos por tipo
const motivosFiltrados = computed(() =>
  motivos.value.filter(m => m.tipo_movimiento === selectedTipo.value)
)

// Detecta nuevo vs existente
const isNew = computed(() => selectedMotivoId.value === 0)
const modalTitle = computed(() =>
  isNew.value ? 'Nuevo Motivo' : 'Gestionar Motivo'
)

// Cuando cambias el select de motivo
watch(selectedMotivoId, id => {
  isEditing.value = id === 0
  if (id && id !== 0) {
    const m = motivos.value.find(x => x.id === id)
    form.value = { ...m }
  } else {
    form.value = {
      id: null,
      codigo: '',
      tipo_movimiento: selectedTipo.value,
      nombre: '',
      descripcion: ''
    }
  }
})
watch(selectedTipo, newTipo => {
  // Vaciamos la selección de motivo
  selectedMotivoId.value = ''

  // (Opcional) Reiniciamos el formulario y el estado de edición
  isEditing.value = false
  form.value = {
    id: null,
    codigo: '',
    tipo_movimiento: newTipo,
    nombre: '',
    descripcion: ''
  }
})

// Habilita edición en existente
function enableEdit() {
  isEditing.value = true
}

// Limpia formulario para nuevo
function clearForm() {
  selectedMotivoId.value = ""
  selectedTipo.value = ''
}

// Borrar motivo
async function deleteMotivo() {
  if (!form.value.id) return
  await api.delete(`/motivos/${form.value.id}`)
  motivos.value = motivos.value.filter(m => m.id !== form.value.id)
  clearForm()
}

// Crear/actualizar motivo
async function saveMotivo() {
  if (isNew.value) {
    const { data } = await api.post('/motivos', form.value)
    motivos.value.push(data)
  } else {
    await api.put(`/motivos/${form.value.id}`, form.value)
    const idx = motivos.value.findIndex(m => m.id === form.value.id)
    motivos.value[idx] = { ...form.value }
  }
  clearForm()
}

// Para emitir eventos al padre
const emit = defineEmits(['saved'])
</script>

<style scoped>
.modal-body { max-height: 60vh; overflow-y: auto; }
</style>